import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Loader2, X, ClipboardList, MapPin, Utensils } from "lucide-react";

const FORMATTER = new Intl.DateTimeFormat("it-IT", {
  weekday: "short", day: "numeric", month: "short",
});

export default function MyOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = async () => {
    try {
      const { data } = await api.get("/orders/mine");
      setOrders(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, []);

  const cancel = async (id) => {
    try {
      await api.post(`/orders/${id}/cancel`);
      toast.success("Prenotazione annullata");
      fetchOrders();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const todays = orders.filter((o) => o.dish_date === today && o.status === "confirmed");
  const past = orders.filter((o) => !(o.dish_date === today && o.status === "confirmed"));

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mb-2">
        Le mie prenotazioni
      </h1>
      <p className="text-muted-foreground mb-8">Gestisci e visualizza lo storico.</p>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento…
        </div>
      ) : (
        <>
          <section className="mb-10">
            <h2 className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-3">
              Oggi
            </h2>
            {todays.length === 0 ? (
              <div className="bg-card border border-border rounded-lg p-6 text-sm text-muted-foreground" data-testid="orders-today-empty">
                Nessuna prenotazione per oggi.
              </div>
            ) : (
              <div className="space-y-3" data-testid="orders-today">
                {todays.map((o) => (
                  <OrderRow key={o.id} order={o} onCancel={cancel} canCancel />
                ))}
              </div>
            )}
          </section>
          <section>
            <h2 className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-3">
              Storico
            </h2>
            {past.length === 0 ? (
              <div className="bg-card border border-border rounded-lg p-6 text-sm text-muted-foreground">
                Nessuna prenotazione passata.
              </div>
            ) : (
              <div className="space-y-3" data-testid="orders-history">
                {past.map((o) => <OrderRow key={o.id} order={o} />)}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function OrderRow({ order, onCancel, canCancel }) {
  const date = order.dish_date ? FORMATTER.format(new Date(order.dish_date)) : "—";
  const total = order.dish_price ? (order.dish_price * order.portions).toFixed(2) : "—";
  return (
    <div className="bg-card border border-border rounded-lg p-4 sm:p-5 flex items-center justify-between gap-4" data-testid={`order-row-${order.id}`}>
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <ClipboardList className={`w-5 h-5 mt-0.5 ${order.status === "cancelled" ? "text-muted-foreground" : "text-primary"}`} />
        <div className="min-w-0">
          <div className="font-display font-medium truncate">{order.dish_name || "—"}</div>
          <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
            <span>{date}</span>
            <span>·</span>
            <span>{order.portions} porz.</span>
            <span>·</span>
            <span>€ {total}</span>
            <span className={`px-1.5 py-0.5 rounded ${
              order.status === "confirmed" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
            }`}>
              {order.status === "confirmed" ? "confermato" : "annullato"}
            </span>
            <span className="text-[10px] uppercase tracking-wide opacity-60">via {order.source}</span>
          </div>
          {order.dish_pickup_location && order.status === "confirmed" && (
            <div className="text-xs mt-1.5 flex items-center gap-1 text-primary" data-testid={`order-pickup-${order.id}`}>
              <MapPin className="w-3 h-3" />
              <span><span className="font-medium">Ritiro:</span> {order.dish_pickup_location}</span>
            </div>
          )}
          {order.needs_cutlery && order.status === "confirmed" && (
            <div className="text-xs mt-1 flex items-center gap-1 text-primary/80" data-testid={`order-cutlery-${order.id}`}>
              <Utensils className="w-3 h-3" />
              <span>Con posate</span>
            </div>
          )}
        </div>
      </div>
      {canCancel && order.status === "confirmed" && (
        <button
          onClick={() => onCancel(order.id)}
          data-testid={`cancel-order-${order.id}`}
          className="px-3 py-1.5 text-sm text-accent border border-accent/30 rounded-md hover:bg-accent hover:text-white transition-colors flex items-center gap-1"
        >
          <X className="w-3.5 h-3.5" /> Annulla
        </button>
      )}
    </div>
  );
}
